import { AuthMachineInterpreter } from '../apps/auth/authMachine'
import { getAuthService } from '../apps/auth/service'
import { localePath } from '../utility/route'

export function useAuthMixin(
  authMounted: () => void | Promise<void> = () => {},
) {
  const authState: Ref<AuthMachineInterpreter['state'] | null> = ref(null)
  const authMachine: Ref<AuthMachineInterpreter | null> = ref(null)

  onMounted(async () => {
    authMachine.value = getAuthService()
    authState.value = authMachine.value.state
    authMachine.value.onTransition(authStateChanged)
    await waitForAuth(authState.value)
    void nextTick(async () => await authMounted())
  })

  async function waitForAuth(authState: AuthMachineInterpreter['state']) {
    if (authState.matches('initialising')) {
      await new Promise((resolve) => {
        authMachine.value.onTransition((state) => {
          if (!state.matches('initialising')) {
            resolve(state)
          }
        })
      })
    }
  }

  const nuxtApp = useNuxtApp()
  const isDesktop = nuxtApp.$isDesktop

  function logOut(redirectTo?: string) {
    authMachine.value?.send('LOG_OUT')
    try {
      if (redirectTo) {
        void useRouter().push(redirectTo)
      } else if (isDesktop) {
        const router = useRouter()
        const route = useRoute()
        const locale = (route.params.lang as string) || 'en-gb'
        void router.push(localePath({ name: 'accounts-login' }, locale, router))
      }
    } catch (error) {
      console.error('Unexpected error during logout:', error || 'Unknown error')
    }
  }

  function logOutInPlace() {
    authMachine.value.send('LOG_OUT')
  }

  const isLoggedIn = computed(() => {
    if (process.server) {
      return false
    }
    return authState.value && authState.value.matches('loggedIn')
  })

  const isLoggingIn = computed(() => {
    if (process.server) {
      return false
    }
    return authState.value && authState.value.matches('loggingIn')
  })

  const isLoggedOut = computed(() => {
    if (process.server) {
      return true
    }
    return !isLoggedIn
  })

  const isLoggingOutSuccess = computed(() => {
    if (process.server) {
      return false
    }
    return authState.value && authState.value.matches('logoutSuccess')
  })

  const isLoggingInSuccess = computed(() => {
    if (process.server) {
      return false
    }
    return authState.value && authState.value.matches('loginSuccess')
  })

  const authHasErrors = computed(() => {
    if (process.server) {
      return false
    }
    return authState.value && authState.value.matches('loggedOut.error')
  })

  const userEmail = computed(() => {
    if (process.server) {
      return ''
    }
    return authState?.value?.context.email
  })

  const isManager = computed(() => {
    if (process.server) {
      return false
    }
    return authState.value?.context.isManager
  })

  function authStateChanged(state: AuthMachineInterpreter['state']) {
    if (state.changed) {
      authState.value = state
    }
  }

  return {
    authState,
    authMachine,
    isLoggedIn,
    isLoggedOut,
    isLoggingIn,
    isLoggingInSuccess,
    isLoggingOutSuccess,
    authHasErrors,
    userEmail,
    isManager,
    logOut,
    logOutInPlace,
  }
}
